import React from 'react';
import { useAuth } from 'react-oidc-context';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertActionLink,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, TrashIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import CenteredSpinner from '@app/CenteredSpinner';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useOrganizationDomain } from '../../../hooks/useOrganizationDomain';
import { useOrganizationDomainResources } from '../../../hooks/useOrganizationDomainResources';
import DeleteResourcesWizard from './DeleteResourcesWizard';

/**
 * "Delete resources" for an organization domain — the wizard's page shell.
 *
 * Authorization (v1): Ancalagon staff only, impersonation allowed so staff can run it for the tenant.
 * A non-staff user who types the URL gets `<NotFound />` — the same answer as a non-existent route, so
 * the feature's existence is never leaked. The detail page hides its entry button on the same check and
 * the backend enforces this authoritatively; this is the frontend mirror
 * (`docs/plans/org-domain-delete-resources-wizard.md` §5.6).
 */
const DomainDeleteResources: React.FC = () => {
  const auth = useAuth();
  const user = useUserContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const domainId = Number(id);

  const isStaff = user?.is_ancalagon_staff === true;
  const {
    domain,
    loading: domainLoading,
    error: domainError,
    notFound,
    refetch: refetchDomain,
  } = useOrganizationDomain(domainId);
  const {
    resources,
    loading: resourcesLoading,
    error: resourcesError,
    notFound: resourcesNotFound,
    refetch: refetchResources,
  } = useOrganizationDomainResources(domainId);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  if (!isStaff) {
    return <NotFound />;
  }

  const detailPath = `/organization/domains/${id}`;

  let crumbName: string;
  if (domain) {
    crumbName = domain.name;
  } else if (notFound) {
    crumbName = 'Not found';
  } else if (domainError) {
    crumbName = 'Error';
  } else {
    crumbName = 'Loading...';
  }

  const renderBody = () => {
    // Full-page spinner only on the initial load: a background refetch (e.g. after an OIDC token
    // renew) keeps the wizard — and the user's selection — mounted instead of blinking into a spinner.
    if (domainLoading && !domain) {
      return <CenteredSpinner />;
    }

    if (notFound) {
      return (
        <EmptyState titleText="Domain not found" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>The requested organization domain does not exist.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/organization/domains" />}>
                Back to domains
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (domainError || !domain) {
      return (
        <EmptyState
          titleText="Could not load domain"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{domainError ?? 'An unexpected error occurred.'}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetchDomain()}>
                Try again
              </Button>
              <Button variant="link" component={(props) => <Link {...props} to={detailPath} />}>
                Back to domain
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    // The wizard is built on the resource inventory: without it there is nothing truthful to show, so
    // the page keeps its chrome and explains itself instead of mounting an empty wizard.
    const inventoryError =
      resourcesError ?? (resourcesNotFound ? 'The requested organization domain does not exist.' : null);

    if (inventoryError !== null) {
      return (
        <Alert
          variant="danger"
          isInline
          component="h2"
          title="Could not list the resources hosted for this domain"
          actionLinks={
            <>
              <AlertActionLink onClick={() => void refetchResources()}>Try again</AlertActionLink>
              <AlertActionLink onClick={() => navigate(detailPath)}>Back to domain</AlertActionLink>
            </>
          }
        >
          <p>{inventoryError}</p>
        </Alert>
      );
    }

    if (resourcesLoading && resources === null) {
      return <CenteredSpinner />;
    }

    if (resources === null) {
      return null;
    }

    return (
      <DeleteResourcesWizard
        domain={domain}
        resources={resources}
        onCancel={() => navigate(detailPath)}
        onRefetchResources={() => void refetchResources()}
      />
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/organization">Organization</BreadcrumbItem>
          <BreadcrumbItem to="/organization/domains">Domains</BreadcrumbItem>
          <BreadcrumbItem to={detailPath}>{crumbName}</BreadcrumbItem>
          <BreadcrumbItem isActive>Delete resources</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <TrashIcon />
          </Icon>
          Delete resources
        </Content>
        <Content component="p">
          Choose which resources Ancalagon hosts for this domain should be removed when the domain leaves.
        </Content>
      </PageSection>

      <PageSection hasBodyWrapper={false}>{renderBody()}</PageSection>
    </>
  );
};

export default DomainDeleteResources;
